import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";import api, { errMsg, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const blank = { name: "", theme: "", description: "", difficulty: "Medium", duration_min: 45, min_players: 3, max_players: 5, image_url: "", base_price: 499, pricing_tiers: [], allow_partial: true, is_active: true };

export default function GamesAdmin() {
  const [games, setGames] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/games").then((r) => setGames(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: `${API}/files/${data.path}` }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };

  const save = async () => {
    try {
      if (editId) await api.put(`/admin/games/${editId}`, form);
      else await api.post("/admin/games", form);
      toast.success("Saved"); setOpen(false); setForm(blank); setEditId(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const edit = (g) => { setForm({ ...blank, ...g, base_price: g.base_price ?? 499 }); setEditId(g.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Delete game?")) return; await api.delete(`/admin/games/${id}`); load(); };

  const addTier = () => setForm((f) => ({ ...f, pricing_tiers: [...(f.pricing_tiers || []), { label: "", min_players: f.min_players, max_players: f.max_players, weekday: f.base_price, weekend: f.base_price, holiday: f.base_price }] }));
  const updTier = (i, k, v) => setForm((f) => { const t = [...(f.pricing_tiers || [])]; t[i] = { ...t[i], [k]: v }; return { ...f, pricing_tiers: t }; });
  const delTier = (i) => setForm((f) => ({ ...f, pricing_tiers: (f.pricing_tiers || []).filter((_, x) => x !== i) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display font-extrabold text-3xl">Games</h1><p className="text-slate-400">Each game runs on its own truck.</p></div>
        <Button data-testid="add-game-btn" onClick={() => { setForm(blank); setEditId(null); setOpen(true); }} className="bg-violet-600 hover:bg-violet-500"><Plus className="h-4 w-4 mr-1" /> New game</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {games.map((g) => (
          <div key={g.id} className="rounded-2xl border border-white/10 bg-[#131a29] overflow-hidden" data-testid={`admin-game-${g.id}`}>
            {g.image_url && <img src={g.image_url} alt="" className="w-full h-32 object-cover" />}
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold">{g.name}</h3>
                <span className="text-xs font-mono text-violet-400">{g.difficulty}</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">{g.theme}</p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="secondary" onClick={() => edit(g)} data-testid={`edit-game-${g.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-red-400" onClick={() => del(g.id)} data-testid={`del-game-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131a29] border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} game</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="game-name" className="bg-slate-950/50 border-white/10" />
            <Input placeholder="Theme" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="bg-slate-950/50 border-white/10" />
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-slate-950/50 border-white/10" />
            <div className="rounded-lg border border-dashed border-white/15 p-3">
              <div className="text-xs text-slate-400 mb-2 font-medium">Game image</div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-slate-950/50 cursor-pointer text-sm" data-testid="game-image-upload">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
                </label>
                {form.image_url && <img src={form.image_url} className="h-14 w-24 rounded object-cover" alt="" />}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                <SelectTrigger className="bg-slate-950/50 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>{["Easy", "Medium", "Hard"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Duration (min)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: +e.target.value })} className="bg-slate-950/50 border-white/10" />
              <Input type="number" placeholder="Min players" value={form.min_players} onChange={(e) => setForm({ ...form, min_players: +e.target.value })} className="bg-slate-950/50 border-white/10" />
              <Input type="number" placeholder="Max players" value={form.max_players} onChange={(e) => setForm({ ...form, max_players: +e.target.value })} className="bg-slate-950/50 border-white/10" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Default / fallback price per person (₹)</label>
              <Input type="number" placeholder="e.g. 499" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: +e.target.value })} data-testid="game-base-price" className="bg-slate-950/50 border-white/10" />
              <p className="text-xs text-slate-500 mt-1">Used when no group tier below matches the team size. New slots default to this price.</p>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer rounded-lg border border-white/10 px-3 py-2.5">
              <Checkbox checked={form.allow_partial !== false} onCheckedChange={(v) => setForm({ ...form, allow_partial: !!v })} data-testid="game-allow-partial" />
              Allow <b className="text-violet-300">25% part-payment</b> at checkout for this game
            </label>

            <div className="rounded-lg border border-white/10 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium">Group pricing — per person</div>
                <Button size="sm" variant="secondary" onClick={addTier} data-testid="add-tier-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add tier</Button>
              </div>
              <p className="text-xs text-slate-500 mb-3">Set a per-person price for each team-size group. <b className="text-slate-300">Fri/Sat/Sun</b> use the Weekend price; admin-defined holidays use the Holiday price.</p>
              <div className="space-y-2">
                {(form.pricing_tiers || []).length > 0 && (
                  <div className="grid grid-cols-12 gap-1 text-[10px] uppercase text-slate-500 px-0.5">
                    <div className="col-span-3">Label</div><div className="col-span-1">Min</div><div className="col-span-1">Max</div>
                    <div className="col-span-2">Weekday</div><div className="col-span-2">Weekend</div><div className="col-span-2">Holiday</div><div className="col-span-1"></div>
                  </div>
                )}
                {(form.pricing_tiers || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`tier-row-${i}`}>
                    <Input className="col-span-3 bg-slate-950/50 border-white/10 h-9 px-2" placeholder="3-4" value={t.label} onChange={(e) => updTier(i, "label", e.target.value)} data-testid={`tier-label-${i}`} />
                    <Input type="number" className="col-span-1 bg-slate-950/50 border-white/10 h-9 px-1.5" value={t.min_players} onChange={(e) => updTier(i, "min_players", +e.target.value)} data-testid={`tier-min-${i}`} />
                    <Input type="number" className="col-span-1 bg-slate-950/50 border-white/10 h-9 px-1.5" value={t.max_players} onChange={(e) => updTier(i, "max_players", +e.target.value)} data-testid={`tier-max-${i}`} />
                    <Input type="number" className="col-span-2 bg-slate-950/50 border-white/10 h-9 px-2" value={t.weekday} onChange={(e) => updTier(i, "weekday", +e.target.value)} data-testid={`tier-weekday-${i}`} />
                    <Input type="number" className="col-span-2 bg-slate-950/50 border-white/10 h-9 px-2" value={t.weekend} onChange={(e) => updTier(i, "weekend", +e.target.value)} data-testid={`tier-weekend-${i}`} />
                    <Input type="number" className="col-span-2 bg-slate-950/50 border-white/10 h-9 px-2" value={t.holiday} onChange={(e) => updTier(i, "holiday", +e.target.value)} data-testid={`tier-holiday-${i}`} />
                    <button className="col-span-1 grid place-items-center text-red-400 hover:text-red-300" onClick={() => delTier(i)} data-testid={`del-tier-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {(form.pricing_tiers || []).length === 0 && <p className="text-xs text-slate-500">No tiers yet — the fallback price above applies to everyone.</p>}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-game" className="bg-violet-600 hover:bg-violet-500">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
